import {
  IMAGE_MAX_DIMENSION,
  IMAGE_QUALITY_STEPS,
  IMAGE_SCALE_STEPS,
} from './MediaCompression.constants';
import { replaceFileExtension } from './MediaCompression.functions';
import {
  eCompressionFailReason,
  tCompressMediaResult,
  tCompressToLimitRequest,
} from './MediaCompression.types';

function canvasToJpegBlob(
  canvas: HTMLCanvasElement,
  quality: number,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
  });
}

/**
 * Re-encodes an image (JPEG/PNG/WebP) as a JPEG that fits within maxBytes:
 * descending JPEG quality first, then progressively smaller dimensions.
 * Transparency is flattened onto white — JPEG has no alpha channel, and
 * without the fill transparent pixels would come out black.
 */
async function compressImageToLimit(
  request: tCompressToLimitRequest,
): Promise<tCompressMediaResult> {
  const { file, maxBytes } = request;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return { ok: false, reason: eCompressionFailReason.PROCESSING_ERROR };
  }

  try {
    const baseScale = Math.min(
      1,
      IMAGE_MAX_DIMENSION / Math.max(bitmap.width, bitmap.height),
    );

    for (const scaleStep of IMAGE_SCALE_STEPS) {
      const scale = baseScale * scaleStep;
      const width = Math.max(1, Math.round(bitmap.width * scale));
      const height = Math.max(1, Math.round(bitmap.height * scale));

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context) {
        return { ok: false, reason: eCompressionFailReason.PROCESSING_ERROR };
      }

      context.fillStyle = '#FFFFFF';
      context.fillRect(0, 0, width, height);
      context.drawImage(bitmap, 0, 0, width, height);

      for (const quality of IMAGE_QUALITY_STEPS) {
        const blob = await canvasToJpegBlob(canvas, quality);

        if (blob && blob.size > 0 && blob.size <= maxBytes) {
          const compressedFile = new File(
            [blob],
            replaceFileExtension(file.name, '.jpg'),
            { type: 'image/jpeg' },
          );

          return {
            ok: true,
            file: compressedFile,
            wasCompressed: true,
            originalBytes: file.size,
            finalBytes: compressedFile.size,
          };
        }
      }
    }

    return { ok: false, reason: eCompressionFailReason.OUTPUT_TOO_LARGE };
  } finally {
    bitmap.close();
  }
}

export const ImageCompressionService = Object.freeze({ compressImageToLimit });
